/* =============================================================================
 * CoachRoute / CoachHome — the /coach/* gate (KGH-795)
 * -----------------------------------------------------------------------------
 * `user_type === 'coach'` is all the router knows. It does not say whether the
 * account finished registering, whether a person has verified it, or whether it
 * has since been suspended — and those are three different screens. Only
 * GET /coach/me answers that, so every coach page is wrapped in this guard and
 * asks once (the answer is cached for two minutes by coachAPI, so moving
 * between coach pages does not re-ask).
 *
 *   403 COACH_ACCOUNT_REQUIRED / COACH_REGISTRATION_INCOMPLETE -> /coach/signup
 *   200 with no coach profile, or access.status 'incomplete' -> /coach/signup
 *   access.can_view_athletes false (pending, rejected, suspended) -> /coach/pending
 *   verified -> the page
 *
 * CoachHome is the same decision without a page attached: it is what `/coach`
 * and any unmatched `/coach/*` path resolve through, so a coach can never land
 * on the family catch-all (which would bounce them to /login, which would
 * silently re-authenticate them back to /coach — a loop with no error message).
 * ========================================================================== */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AppLayout from './AppLayout';
import ErrorState from './ErrorState';
import { Card, Skeleton } from './ui';
import { coachPageStyle, PANEL_MEASURE } from './coach/pageLayout';
import { useResponsive } from '../hooks/useResponsive';
import { coachAPI, isCoachApiError, isCoachSetupUnfinished } from '../services/coachAPI';
import { space } from '../styles/tokens';

/**
 * Where a verified coach belongs. It must always name a route that is actually
 * mounted in App.tsx: `/coach/*` resolves through CoachHome, so pointing this
 * at an unmounted path would send a coach straight back here.
 */
export const COACH_HOME_PATH = '/coach/players';

type Resolution =
  | { state: 'loading' }
  | { state: 'ok' }
  | { state: 'signup' }
  | { state: 'pending' }
  | { state: 'error'; message: string };

function useCoachAccess(): [Resolution, () => void] {
  const [resolution, setResolution] = useState<Resolution>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setResolution({ state: 'loading' });
    coachAPI
      .me()
      .then((res) => {
        if (cancelled) return;
        if (res.access?.can_view_athletes) setResolution({ state: 'ok' });
        // An account with no coach profile yet, or one that never got its
        // program, finishes on sign-up, same as the 403 codes below.
        else if (isCoachSetupUnfinished(res)) setResolution({ state: 'signup' });
        else setResolution({ state: 'pending' });
      })
      .catch((e) => {
        if (cancelled) return;
        const code = isCoachApiError(e) ? String(e.code) : '';
        if (code === 'COACH_ACCOUNT_REQUIRED' || code === 'COACH_REGISTRATION_INCOMPLETE') {
          setResolution({ state: 'signup' });
          return;
        }
        if (
          code === 'COACH_VERIFICATION_PENDING' ||
          code === 'COACH_VERIFICATION_REJECTED' ||
          code === 'COACH_ACCOUNT_SUSPENDED'
        ) {
          setResolution({ state: 'pending' });
          return;
        }
        setResolution({
          state: 'error',
          message: isCoachApiError(e) ? e.message : 'We could not reach your coach account.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return [resolution, () => setAttempt((n) => n + 1)];
}

const CoachBootSkeleton: React.FC = () => {
  const { isMobile } = useResponsive();
  return (
    // consumeDeepLinks off: the page's own layout replaces this one the moment
    // GET /coach/me answers. If this one acted on ?openSettings=true it would
    // strip the param, and the page's layout would mount with the modal closed.
    <AppLayout consumeDeepLinks={false}>
      {/* This skeleton stands in front of EVERY coach page for as long as
          GET /coach/me takes, so it uses the same house container those pages
          use (was 820). A boot placeholder that is 780px narrower than the page
          it precedes makes every coach page open with a jolt. */}
      <div style={coachPageStyle(isMobile)}>
        <Skeleton variant="line" height={26} width="45%" aria-label="Opening the coach portal" />
        <div style={{ height: space[5] }} />
        <Card padding={20}>
          <Skeleton variant="line" height={16} width="35%" />
          <div style={{ height: space[3] }} />
          <Skeleton variant="rect" height={110} />
        </Card>
      </div>
    </AppLayout>
  );
};

export interface CoachRouteProps {
  children: React.ReactElement;
}

/** Wraps a page that only a VERIFIED coach may see. */
export const CoachRoute: React.FC<CoachRouteProps> = ({ children }) => {
  const [resolution, retry] = useCoachAccess();
  const { isMobile } = useResponsive();

  if (resolution.state === 'loading') return <CoachBootSkeleton />;
  if (resolution.state === 'signup') return <Navigate to="/coach/signup" replace />;
  if (resolution.state === 'pending') return <Navigate to="/coach/pending" replace />;
  if (resolution.state === 'error') {
    return (
      // Transient like the skeleton: leave ?openSettings=true for the page a
      // retry mounts.
      <AppLayout consumeDeepLinks={false}>
        {/* CENTRED PANEL, not a page of content: the gate failed, so there is
            no page behind this. House container (was 820), panel measure
            inside it. */}
        <div style={coachPageStyle(isMobile)}>
          <div style={{ maxWidth: PANEL_MEASURE, margin: '0 auto' }}>
            <ErrorState message={resolution.message} onRetry={retry} retryLabel="Try again" />
          </div>
        </div>
      </AppLayout>
    );
  }
  return children;
};

/** `/coach` and every unmatched `/coach/*` path: send the coach where they belong. */
export const CoachHome: React.FC = () => {
  const [resolution, retry] = useCoachAccess();
  const { isMobile } = useResponsive();
  // Carry ?openSettings=true through the redirect so /coach?openSettings=true
  // opens settings on the page it lands on. Other params are not carried.
  const { search } = useLocation();
  const openSettings = new URLSearchParams(search).get('openSettings') === 'true';

  if (resolution.state === 'loading') return <CoachBootSkeleton />;
  if (resolution.state === 'signup') return <Navigate to="/coach/signup" replace />;
  if (resolution.state === 'pending') return <Navigate to="/coach/pending" replace />;
  if (resolution.state === 'error') {
    return (
      // Transient like the skeleton: leave ?openSettings=true for the page a
      // retry mounts.
      <AppLayout consumeDeepLinks={false}>
        {/* CENTRED PANEL, not a page of content: the gate failed, so there is
            no page behind this. House container (was 820), panel measure
            inside it. */}
        <div style={coachPageStyle(isMobile)}>
          <div style={{ maxWidth: PANEL_MEASURE, margin: '0 auto' }}>
            <ErrorState message={resolution.message} onRetry={retry} retryLabel="Try again" />
          </div>
        </div>
      </AppLayout>
    );
  }
  return <Navigate to={openSettings ? `${COACH_HOME_PATH}?openSettings=true` : COACH_HOME_PATH} replace />;
};

export default CoachRoute;
